import { useMutation, useQuery } from '@tanstack/react-query';
import {
  formatMoney,
  type JobProforma,
  type JobProformaCandidatesResponse,
} from '@k-management/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';

/**
 * The pro-forma linked to a cost (3.26; K-Rev issues no pro-forma, so the rate show none): it changes only what the
 * Scadenzario counts, never the figures of the commessa. `base` is the path of
 * the rata or the cost, e.g. `/jobs/<id>/installments/<id>`.
 */
export function ProformaCell<T>({
  base,
  proforma,
  editable,
  subjectLabel,
  onChanged,
}: {
  base: string;
  proforma: JobProforma | null;
  editable: boolean;
  /** «cliente» for a rata, «fornitore» for a cost. */
  subjectLabel: string;
  onChanged: (data: T) => unknown;
}) {
  const canTreasury = useCan('tesoreria.read');
  // The pro-forma are Tesoreria data: without it nothing to choose or change here.
  const canChange = editable && canTreasury;
  const [choosing, setChoosing] = useState(false);
  const unlink = useMutation({
    mutationFn: () =>
      apiRequest<T>(`${base}/proforma`, { method: 'PUT', body: { proformaId: null } }),
    onSuccess: onChanged,
  });

  if (!proforma) {
    return (
      <>
        {canChange ? (
          <button type="button" className="link small" onClick={() => setChoosing(true)}>
            Collega una pro-forma
          </button>
        ) : (
          <span className="muted">—</span>
        )}
        {choosing && (
          <ProformaDialog
            base={base}
            subjectLabel={subjectLabel}
            onClose={() => setChoosing(false)}
            onChanged={async (data: T) => {
              setChoosing(false);
              await onChanged(data);
            }}
          />
        )}
      </>
    );
  }

  const label = `PF ${proforma.number} del ${formatDate(proforma.date)} · ${formatMoney(proforma.total)}`;
  return (
    <>
      <div>
        {canTreasury ? <Link to={`/tesoreria/fatture/${proforma.id}`}>{label}</Link> : label}
      </div>
      {proforma.state === 'attiva' && <span className="pill info">collegata</span>}
      {proforma.state === 'sostituita' && (
        <div className="muted small">
          sostituita dalla fattura {proforma.replacedBy ?? ''}: il collegamento non ha più effetto
        </div>
      )}
      {proforma.state === 'annullata' && (
        <div className="muted small">annullata: il collegamento non ha più effetto</div>
      )}
      {canChange && (
        <button
          type="button"
          className="link small"
          disabled={unlink.isPending}
          onClick={() => unlink.mutate()}
        >
          Scollega
        </button>
      )}
      {unlink.isError && <p className="error">{unlink.error.message}</p>}
    </>
  );
}

function ProformaDialog<T>({
  base,
  subjectLabel,
  onClose,
  onChanged,
}: {
  base: string;
  subjectLabel: string;
  onClose: () => void;
  onChanged: (data: T) => unknown;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  const candidates = useQuery({
    queryKey: ['job-proforma-candidates', base],
    queryFn: () => apiRequest<JobProformaCandidatesResponse>(`${base}/proforma-candidates`),
  });
  const link = useMutation({
    mutationFn: () =>
      apiRequest<T>(`${base}/proforma`, { method: 'PUT', body: { proformaId: chosen } }),
    onSuccess: onChanged,
  });
  const list = candidates.data?.candidates ?? [];

  return (
    <ConfirmDialog
      title="Collega una pro-forma"
      confirmLabel="Collega"
      busy={link.isPending}
      disabled={!chosen}
      error={link.isError ? link.error.message : null}
      onConfirm={() => link.mutate()}
      onCancel={onClose}
      wide
    >
      {candidates.isError ? (
        <p className="error">{candidates.error.message}</p>
      ) : !candidates.data ? (
        <p className="muted">Caricamento…</p>
      ) : list.length === 0 ? (
        <p className="muted">
          Nessuna pro-forma libera di questo {subjectLabel}: serve una pro-forma non ancora
          sostituita da una fattura né collegata ad altro.
        </p>
      ) : (
        <>
          <p>
            Pro-forma aperte del {subjectLabel}, non ancora sostituite da una fattura. Nello
            Scadenzario si conta l’imponibile della pro-forma al posto di quello{' '}
            {subjectLabel === 'cliente' ? 'della rata' : 'del costo'}: fatturato e margini della
            commessa non cambiano.
          </p>
          <div className="table-scroll">
            <table className="lines">
              <thead>
                <tr>
                  <th />
                  <th>Pro-forma</th>
                  <th>Data</th>
                  <th className="right">Imponibile</th>
                  <th className="right">Totale</th>
                </tr>
              </thead>
              <tbody>
                {list.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <input
                        type="radio"
                        name="proforma"
                        aria-label={`Pro-forma ${item.number}`}
                        checked={chosen === item.id}
                        onChange={() => setChosen(item.id)}
                      />
                    </td>
                    <td>{item.number}</td>
                    <td className="nowrap">{formatDate(item.date)}</td>
                    <td className="right nowrap">{formatMoney(item.taxable)}</td>
                    <td className="right nowrap">{formatMoney(item.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </ConfirmDialog>
  );
}
